<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h2>Gestión de Preguntas</h2>
          <v-btn
            v-if="canCreateQuestions"
            color="primary"
            @click="$router.push('/questions/create')"
            prepend-icon="mdi-plus"
          >
            Nueva Pregunta
          </v-btn>
        </div>

        <!-- Filtros -->
        <v-card class="mb-4" elevation="2">
          <v-card-title>
            <v-icon class="mr-2">mdi-filter</v-icon>
            Filtros
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="filters.school_subject_id"
                  :items="schoolSubjects"
                  item-title="name"
                  item-value="id"
                  label="Asignatura"
                  clearable
                  variant="outlined"
                  density="compact"
                  :loading="schoolSubjectsLoading"
                  hint="Escriba para buscar o seleccione la asignatura"
                  persistent-hint
                  no-data-text="No se encontraron asignaturas"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>mdi-book-search</v-icon>
                  </template>
                </v-autocomplete>
              </v-col>
              <!-- <v-col cols="12" md="4" v-if="!isStudent">
                <v-select
                  v-model="filters.author"
                  :items="authors"
                  item-title="name"
                  item-value="id"
                  label="Autor"
                  clearable
                  variant="outlined"
                  density="compact"
                />
              </v-col> -->
              <v-col cols="12" md="4">
                <v-select
                  v-model="filters.school_grade_id"
                  :items="schoolGrades"
                  item-title="gradeLabel"
                  item-value="id"
                  label="Grado Escolar"
                  clearable
                  variant="outlined"
                  density="compact"
                  filterable
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="d-flex gap-2">
                <v-btn
                  color="primary"
                  @click="applyFilters"
                  :loading="loading"
                  prepend-icon="mdi-magnify"
                >
                  Buscar
                </v-btn>
                <v-btn
                  color="secondary"
                  variant="outlined"
                  @click="clearFilters"
                  prepend-icon="mdi-filter-remove"
                >
                  Limpiar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Tabla de preguntas -->
        <v-data-table
          :headers="headers"
          :items="questions"
          :loading="loading"
          :server-items-length="pagination.total"
          :items-per-page="pagination.limit"
          :page="pagination.page"
          @update:options="updatePagination"
          loading-text="Cargando preguntas..."
          no-data-text="No hay preguntas disponibles"
          class="elevation-2"
        >
          <template v-slot:item.statement="{ item }">
            <div class="question-statement">
              <MathRenderer :content="truncateText(item.statement, 100)" />
            </div>
          </template>

          <template v-slot:item.school_subject="{ item }">
            <v-chip
              v-if="item.school_subject?.name"
              color="primary"
              size="small"
              variant="outlined"
            >
              {{ item.school_subject.name }}
            </v-chip>
            <span v-else class="text-disabled">Sin asignatura</span>
          </template>

          <template v-slot:item.school_grade="{ item }">
            <v-chip v-if="item.school_grade?.grade" color="info" size="small" variant="outlined">
              Grado {{ item.school_grade.grade }}
            </v-chip>
            <span v-else class="text-disabled">Sin grado</span>
          </template>

          <template v-slot:item.author="{ item }" v-if="!isStudent">
            <div class="d-flex align-center">
              <v-icon class="mr-2" size="16">mdi-account</v-icon>
              ID: {{ item.author_id || 'N/D' }}
            </div>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
              <!-- Ver/Responder pregunta -->
              <v-btn
                v-if="isStudent"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="answerQuestion(item)"
                :title="'Responder pregunta'"
              >
                <v-icon>mdi-play</v-icon>
              </v-btn>

              <!-- Ver detalles -->
              <v-btn
                icon
                size="small"
                color="info"
                variant="text"
                @click="viewDetails(item)"
                :title="'Ver detalles'"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>

              <!-- Editar (solo profesor owner o admin) -->
              <v-btn
                v-if="canEdit(item)"
                icon
                size="small"
                color="warning"
                variant="text"
                @click="editQuestion(item)"
                :title="'Editar pregunta'"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <!-- Eliminar (solo profesor owner o admin) -->
              <v-btn
                v-if="canDelete(item)"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item)"
                :title="'Eliminar pregunta'"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Dialog de confirmación para eliminar -->
    <ConfirmDialog
      v-model="deleteDialog"
      title="Confirmar eliminación"
      text="¿Está seguro que desea eliminar esta pregunta? Esta acción no se puede deshacer."
      confirm-text="Eliminar"
      color="error"
      :loading="deleteLoading"
      @confirmed="handleDeleteQuestion"
      @cancelled="deleteDialog = false"
    />

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import MathRenderer from '@/components/common/MathRenderer.vue'

export default {
  name: 'QuestionList',

  components: {
    ConfirmDialog,
    MathRenderer,
  },

  data() {
    return {
      deleteDialog: false,
      deleteLoading: false,
      questionToDelete: null,

      filters: {
        school_subject_id: null,
        author: null,
        difficulty: null,
        school_grade_id: null,
      },

      authors: [], // Se cargará dinámicamente
      schoolGrades: [], // Se cargará dinámicamente

      difficulties: [
        { label: 'Fácil', value: 'easy' },
        { label: 'Medio', value: 'medium' },
        { label: 'Difícil', value: 'hard' },
      ],

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    }
  },

  computed: {
    ...mapGetters('questions', ['questions', 'loading', 'pagination', 'error']),
    ...mapGetters('auth', ['isStudent', 'isTeacher', 'isAdmin', 'user']),
    ...mapGetters('schoolSubjects', {
      allSubjects: 'allSubjects',
      schoolSubjectsLoading: 'isLoading',
    }),

    schoolSubjects() {
      return this.allSubjects
    },

    canCreateQuestions() {
      return this.isTeacher || this.isAdmin
    },

    headers() {
      const baseHeaders = [
        { title: 'Enunciado', key: 'statement', sortable: false, width: '35%' },
        { title: 'Asignatura', key: 'school_subject', sortable: true },
        { title: 'Grado Escolar', key: 'school_grade', sortable: true },
        { title: 'Fecha de creación', key: 'created_at', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false, align: 'center' },
      ]

      if (!this.isStudent) {
        baseHeaders.splice(4, 0, { title: 'Autor', key: 'author', sortable: false })
      }

      return baseHeaders
    },
  },

  watch: {
    error(newError) {
      if (newError) {
        this.showSnackbar(newError, 'error')
      }
    },
  },

  async created() {
    await Promise.all([this.loadSchoolGrades(), this.loadSchoolSubjects(), this.loadQuestions()])
  },

  methods: {
    ...mapActions('questions', [
      'fetchQuestions',
      'deleteQuestion',
      'setFilters',
      'resetFilters',
      'changePage',
      'clearError',
    ]),
    ...mapActions('schoolGrades', ['fetchGrades']),
    ...mapActions('schoolSubjects', ['fetchAllSubjects']),

    async loadQuestions() {
      const result = await this.fetchQuestions()
      if (!result.success) {
        this.showSnackbar(result.error, 'error')
      }
    },

    async loadSchoolGrades() {
      try {
        const result = await this.fetchGrades()
        if (result.success) {
          this.schoolGrades = result.data.map((grade) => ({
            ...grade,
            gradeLabel: `Grado ${grade.grade}`,
          }))
        }
      } catch (error) {
        console.error('Error loading school grades:', error)
      }
    },

    async loadSchoolSubjects() {
      try {
        const result = await this.fetchAllSubjects()
        if (!result.success) {
          console.error('Error loading school subjects:', result.error)
        } else {
          console.log(`School subjects loaded: ${this.schoolSubjects.length}`)
        }
      } catch (error) {
        console.error('Error loading school subjects:', error)
      }
    },

    async applyFilters() {
      const result = await this.setFilters(this.filters)
      if (!result.success) {
        this.showSnackbar(result.error, 'error')
      }
    },

    async clearFilters() {
      this.filters = {
        school_subject_id: null,
        //author: null,
        //difficulty: null,
        school_grade_id: null,
      }
      const result = await this.resetFilters()
      if (!result.success) {
        this.showSnackbar(result.error, 'error')
      }
    },

    async updatePagination(options) {
      if (options.page !== this.pagination.page) {
        const result = await this.changePage(options.page)
        if (!result.success) {
          this.showSnackbar(result.error, 'error')
        }
      }
    },

    canEdit(question) {
      if (this.isAdmin) return true
      if (this.isTeacher && question.author?.id === this.user?.id) return true
      return false
    },

    canDelete(question) {
      if (this.isAdmin) return true
      if (this.isTeacher && question.author?.id === this.user?.id) return true
      return false
    },

    viewDetails(question) {
      this.$router.push(`/questions/${question.id}`)
    },

    answerQuestion(question) {
      this.$router.push(`/questions/${question.id}/answer`)
    },

    editQuestion(question) {
      this.$router.push(`/questions/${question.id}/edit`)
    },

    confirmDelete(question) {
      this.questionToDelete = question
      this.deleteDialog = true
    },

    async handleDeleteQuestion() {
      if (!this.questionToDelete) return

      this.deleteLoading = true

      try {
        const result = await this.deleteQuestion(this.questionToDelete.id)

        if (result.success) {
          this.deleteDialog = false
          this.showSnackbar('Pregunta eliminada exitosamente', 'success')
          this.questionToDelete = null
        } else {
          this.showSnackbar(result.error, 'error')
        }
      } catch (error) {
        this.showSnackbar('Error al eliminar la pregunta', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },

    getDifficultyColor(difficulty) {
      const colors = {
        easy: 'success',
        medium: 'warning',
        hard: 'error',
      }
      return colors[difficulty] || 'grey'
    },

    getDifficultyLabel(difficulty) {
      const labels = {
        easy: 'Fácil',
        medium: 'Medio',
        hard: 'Difícil',
      }
      return labels[difficulty] || difficulty
    },

    formatDate(dateString) {
      if (!dateString) return 'N/D'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES')
    },

    showSnackbar(message, color = 'success', timeout = 4000) {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout,
      }
    },
  },
}
</script>

<style scoped>
.question-statement {
  max-width: 300px;
  overflow: hidden;
  max-height: 3em;
  line-height: 1.5em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Asegurar que el contenido de MathRenderer se muestre correctamente */
.question-statement :deep(.math-renderer) {
  display: inline;
}

.d-flex.gap-1 > * {
  margin-right: 4px;
}

.d-flex.gap-2 > * {
  margin-right: 8px;
}
</style>
